import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/useToast";
import { useAuthStore } from "@/store";
import { Bug, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const bugCategories = [
  { value: "ui-ux", label: "UI/UX Issues" },
  { value: "performance", label: "Performance Problems" },
  { value: "functionality", label: "Feature Not Working" },
  { value: "authentication", label: "Login/Authentication" },
  { value: "file-upload", label: "File Upload Issues" },
  { value: "ai-responses", label: "AI Response Problems" },
  { value: "chat", label: "Chat Interface" },
  { value: "settings", label: "Settings/Configuration" },
  { value: "mobile", label: "Mobile Issues" },
  { value: "other", label: "Other" },
];

export function BugReportDialog({ open, onOpenChange }) {
  const { profile } = useAuthStore();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    category: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (open && profile) {
      setFormData({
        name:
          profile.full_name || profile.display_name || profile.username || "",
        email: profile.email || "",
        category: "",
        message: "",
      });

      // Focus message field after a short delay
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onOpenChange(false);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.message.trim() || !formData.category || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/notifications/slack/bug-report`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Bug Report Sent",
          description:
            "Thank you for your report! We'll look into it as soon as possible.",
        });

        // Reset and close
        setFormData((prev) => ({ ...prev, message: "", category: "" }));
        onOpenChange(false);
      } else {
        throw new Error(result.error || "Failed to send bug report");
      }
    } catch (error) {
      console.error("Bug report submission error:", error);
      toast({
        title: "Submission Failed",
        description:
          error.message ||
          "There was an error sending your report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (
      e.key === "Enter" &&
      e.ctrlKey &&
      formData.message.trim() &&
      formData.category &&
      !isSubmitting
    ) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape" && !isSubmitting) {
      e.preventDefault();
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[90vw] max-h-[90vh] w-[500px] h-auto bg-white dark:bg-zinc-900 border-gray-200 dark:border-[#3a3a3a] text-gray-900 dark:text-white p-0 gap-0 rounded-2xl flex flex-col overflow-hidden shadow-2xl"
        hideCloseButton={true}
        onEscapeKeyDown={(e) => isSubmitting && e.preventDefault()}
        onPointerDownOutside={(e) => isSubmitting && e.preventDefault()}
      >
        <DialogTitle className="sr-only">Report Bug</DialogTitle>
        <DialogDescription className="sr-only">
          Report a bug or issue you&apos;ve encountered with ArtifactChat
        </DialogDescription>

        {/* Header */}
        <div className="flex items-center justify-between py-3 px-4 bg-white dark:bg-zinc-900 flex-shrink-0 border-b border-gray-100 dark:border-white/5">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Report Bug
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-900">
          <div className="flex-1 overflow-auto px-4 py-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label
                  htmlFor="bug-name"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Name
                </label>
                <input
                  id="bug-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  disabled={isSubmitting}
                  className="h-9 w-full rounded-lg border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-gray-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-purple-400/50 transition-all"
                />
              </div>
              <div className="space-y-1.5">
                <label
                  htmlFor="bug-email"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Email
                </label>
                <input
                  id="bug-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  disabled={isSubmitting}
                  className="h-9 w-full rounded-lg border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-gray-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-purple-400/50 transition-all"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="bug-category"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Bug Category
              </label>
              <div className="relative">
                <select
                  id="bug-category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className="h-9 w-full px-3 bg-white dark:bg-zinc-800 border border-gray-300 dark:border-zinc-700 rounded-lg text-sm text-gray-900 dark:text-white appearance-none focus:outline-none focus:ring-2 focus:ring-purple-400/50 transition-all cursor-pointer"
                >
                  <option value="" disabled>
                    Select bug category
                  </option>
                  {bugCategories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.5 4.5L6 8L9.5 4.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="bug-message"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Bug Description
              </label>
              <textarea
                ref={textareaRef}
                id="bug-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Please describe the issue in detail..."
                disabled={isSubmitting}
                rows={4}
                className="w-full resize-none rounded-lg border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-gray-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-purple-400/50 transition-all"
              />
            </div>

            {/* Info Box */}
            <div className="flex items-start gap-3 rounded-xl bg-gray-50 dark:bg-zinc-800/50 p-3 border border-gray-100 dark:border-white/5">
              <div className="text-purple-500 dark:text-purple-400 mt-0.5">
                <Bug className="w-4 h-4" />
              </div>
              <div>
                <strong className="text-gray-700 dark:text-gray-200 block text-xs font-semibold">
                  Help us squash bugs
                </strong>
                <p className="text-gray-500 dark:text-gray-400 text-[11px] leading-relaxed">
                  Your reports help us make ArtifactChat better for everyone.
                  Thank you for your patience!
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50/50 dark:bg-zinc-900/50 border-t border-gray-100 dark:border-white/5 flex-shrink-0">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-zinc-800 px-3 py-2 rounded-lg text-sm font-medium"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                isSubmitting || !formData.message.trim() || !formData.category
              }
              className="bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 px-4 rounded-full text-sm font-semibold h-9 shadow-sm transition-all"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <span>Sending...</span>
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : (
                "Report Bug"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default BugReportDialog;
